import React, { useEffect } from 'react';
import { Route, Navigate, Routes, useNavigate, useLocation } from 'react-router-dom';
import PrivateRoute from '@components/PrivateRoute';
import { BaselineComparisonPage, DashboardPage, GreenpactLivePage, LoginPage, ReportsPage } from '@pages';
import { APP_NAME, FEEDBACK_EMAIL } from '@constants/index';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { localeEn, setOptions } from '@mobiscroll/react';
import './mobiscrollTheme.sass';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSideMenuOpen,
  getThemeMode,
  setSideMenuOpen,
  setThemeMode,
  getDropDownMenuOpen,
  resetSession,
  getDisplayName
} from '@store/features';
import { logout } from '@store/helpers';
import { getSideMenuList } from './SideMenuList';
import Swal from 'sweetalert2';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import { useTheme } from '@hooks';
import { EsgianTheme } from '@esgian/esgianui';
import { resetMainSlice } from '@store/features/filters/MainPageSlice/MainPageSlice';
import { resetBaselineSlice } from '@store/features/filters/BaselineComparisonSlice/BaselineComparisonSlice';
import TestPage from '@pages/TestPage';
import { logEvent } from './api/Logging';
import { Dashboard } from '@components/Drawers/Dashboard';
import { useLoggingServices } from '@hooks/useLoggingServices';
import { useAuth } from 'react-oidc-context';
import { MOCK, signoutState } from '@auth/userManager';
import { LoadingScreen } from '@components';
import { getConfig } from '@config/runtimeConfig';

mapboxgl.accessToken = getConfig('MAPBOX_API_TOKEN')

setOptions({
  locale: localeEn,
  theme: 'ios',
  themeVariant: 'light'
});

// OIDC redirect landing. react-oidc-context processes the ?code/&state here; once done we must
// move on via react-router's <Navigate>
function AuthCallback() {
  const auth = useAuth();
  if (auth.error) {
    return <Navigate to="/login" replace />;
  }
  if (auth.isLoading || !auth.isAuthenticated) {
    return <LoadingScreen />;
  }
  return <Navigate to={auth.user?.state?.returnTo || '/'} replace />;
}

function App() {
  const dispatch = useDispatch();
  const auth = useAuth();
  const sideMenuOpen = useSelector(getSideMenuOpen);
  const dropDownMenuOpen = useSelector(getDropDownMenuOpen);
  const themeMode = useSelector(getThemeMode);
  const { theme } = useTheme();
  const displayName = useSelector(getDisplayName);
  let navigate = useNavigate();
  const { logPageChange } = useLoggingServices();

  const routeChange = (path) => {
    logEvent(`Page change. Navigating to ${path}.`, undefined, { path });
    navigate(path);
  };

  const location = useLocation();
  useEffect(() => {
    try {
      const path = `${location.pathname}${location.search ?? ''}`;
      logPageChange(path);
    } catch (err) {
      console.error('Route logging failed', err);
    }
  }, [location.pathname, location.search]);

  const handleLogout = async () => {
    Swal.fire({ title: 'Signing out...' });
    Swal.showLoading();
    // Block PrivateRoute's auto sign-in AND its session fetch for the whole logout — including
    // the window after react-oidc-context clears activeNavigator (its finally) but before the
    // browser leaves. Set before resetSession(), which puts the session back to 'idle' and would
    // otherwise make PrivateRoute fire a doomed /me request while the token is being cleared.
    // Not cleared on the success path: the page reloads when logout returns to /login, which
    // resets signoutState. See @auth/userManager.
    if (!MOCK) {
      signoutState.inProgress = true;
    }
    logout();
    dispatch(resetSession());
    dispatch(resetMainSlice());
    dispatch(resetBaselineSlice());
    Swal.close();
    if (!MOCK) {
      try {
        // react-oidc-context's wrapper (NOT userManager directly) so activeNavigator is set too.
        await auth.signoutRedirect();
      } catch (err) {
        // No reachable end-session endpoint (or it errored). The local user is already cleared by
        // signoutRedirect before it throws; re-enable sign-in (no page reload on this path) and
        // land on the public login page.
        console.error('OIDC signout redirect failed', err);
        signoutState.inProgress = false;
        routeChange('/login');
      }
    } else {
      routeChange('/login');
    }
  };

  // Prefer the OIDC `name` claim (full display name, e.g. "John Doe")
  const displayNameForMenu = auth.user?.profile?.name || displayName;
  const sessionUser = displayNameForMenu
    ? { profile: { firstName: displayNameForMenu, company: null } }
    : null;

  return (
    <EsgianTheme app={APP_NAME} mode={theme.mode}>
      <Dashboard
        feedbackEmail={FEEDBACK_EMAIL}
        sideMenuContent={getSideMenuList(routeChange, sideMenuOpen, dropDownMenuOpen, dispatch)}
        appTitle={'OX Energy Management'}
        app={APP_NAME}
        handleLogoutClick={handleLogout}
        hideMenu={!auth.isAuthenticated && !MOCK}
        user={sessionUser}
        isAuthenticated={auth.isAuthenticated || MOCK}
        handleLoginClick={() => auth.signinRedirect()}
        handleThemeChange={() => {
          dispatch(setThemeMode(!themeMode));
        }}
        themeMode={theme.mode}
        sideMenuOpen={!!sideMenuOpen}
        setSideMenuOpen={(val) => {
          dispatch(setSideMenuOpen(val));
        }}>
        <ToastContainer
          toastStyle={{ background: theme.palette.background.paper }}
          position="top-right"
          autoClose={3000}
          theme={theme.mode}
        />
        <Routes>
          <Route path="*" element={<Navigate to="/" />} />
          <Route path="/login" exact element={<LoginPage />} />
          <Route path="/callback" element={<AuthCallback />} />
          <Route
            exact
            path="/"
            element={
              <PrivateRoute>
                <DashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path="/rig"
            element={
              <PrivateRoute>
                <GreenpactLivePage />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path="/baseline-comp"
            element={
              <PrivateRoute>
                <BaselineComparisonPage />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path="/test"
            element={
              <PrivateRoute>
                <TestPage />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path="/reports"
            element={
              <PrivateRoute>
                <ReportsPage />
              </PrivateRoute>
            }
          />
        </Routes>
      </Dashboard>
    </EsgianTheme>
  );
}

export default App;
